import { useReveal } from "../hooks/useReveal";
import { Quote, Heart } from "lucide-react";
import { Button } from "../components/ui/button";

const FOUNDER_IMG =
  "https://images.pexels.com/photos/7551677/pexels-photo-7551677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";

export default function FounderStory() {
  const [ref, visible] = useReveal();
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="relative py-24 md:py-32 bg-gradient-to-br from-[#FFFFFF] via-[#F8F9FA] to-[#FFFFFF] overflow-hidden"
    >
      <div className="absolute inset-0 grain" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Portrait + decorative */}
        <div className="lg:col-span-5 relative order-1 lg:order-1">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(0,81,101,0.3)]">
            <img
              src={FOUNDER_IMG}
              alt="The founder of Madison Peaceful Path Home Care"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#005165]/30 via-transparent to-transparent" />
          </div>
          <div className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 rounded-full border-2 border-[#AC9667]/40" />
          <div className="hidden md:block absolute -top-4 -right-4 w-20 h-20 rounded-full bg-[#AC9667]/15 backdrop-blur" />
          <div className="absolute -bottom-8 right-4 sm:right-10 bg-white rounded-2xl p-5 sm:p-6 shadow-xl border border-[#E5DDC9] max-w-[300px] float-y">
            <Heart className="w-5 h-5 text-[#AC9667] mb-2 fill-[#AC9667]" />
            <p className="text-[14px] text-[#1F2937] italic leading-snug">
              "Care that feels personal. Care that respects the individual. Care that brings peace of mind."
            </p>
          </div>
        </div>

        {/* Story */}
        <div
          ref={ref}
          className={`lg:col-span-7 order-2 lg:order-2 ${visible ? "reveal visible" : "opacity-0"}`}
        >
          <span className="gold-divider mb-5">Our Founder's Story</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#005165] leading-[1.08]">
            The heart behind
            <span className="block italic text-[#AC9667] font-light">Madison Peaceful Path.</span>
          </h2>

          <div className="mt-8 space-y-5 text-[17px] text-[#1F2937] leading-relaxed">
            <p>
              <span className="font-serif text-[#AC9667] text-2xl">M</span>adison Peaceful Path Home
              Care was created from a lifetime of caring, compassion, and a deep understanding of
              what it truly means to support someone you love.
            </p>
            <p>
              Our founder's passion for caregiving began in her own home. Growing up with two deaf
              parents, she learned early in life the power of patience, the importance of clear
              communication, the meaning of advocacy, and the quiet strength of simply{" "}
              <em className="text-[#005165] not-italic font-medium">showing up</em> for the people
              who need you.
            </p>
            <p>
              Caring was never a job — it was always part of her life. From helping family members
              navigate daily challenges to supporting people throughout her professional career, she
              spent years in nursing homes and healthcare environments, including leadership roles.
              And every step of the way she saw the same truth: seniors needed more than basic
              assistance.
            </p>
            <p className="text-[#005165] font-serif text-xl md:text-2xl italic border-l-2 border-[#AC9667] pl-5">
              They needed dignity. They needed comfort. They needed connection. They needed someone
              they could trust.
            </p>
            <p>
              Madison Peaceful Path Home Care was created to give families something deeper than
              services on a checklist — to give them care that feels personal, care that respects
              the individual, and care that brings real peace of mind.
            </p>
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <Button
              data-testid="founder-cta-consult"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[#005165] hover:bg-[#003946] text-white rounded-full h-12 px-7 btn-shimmer"
            >
              Begin Your Family's Story With Us
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
